import React from "react";
import { BlockMath, InlineMath } from "react-katex";
import TheoryExamplesRunner from "../../components/TheoryExamplesRunner";

export default function DescriptiveStatisticsPreparationTheory() {
  const intro = (
    <div className="space-y-3">
      <p>
        W zadaniach maturalnych ze statystyki najczęściej obliczamy
        <strong> średnią arytmetyczną</strong>, <strong>medianę</strong> albo
        <strong> dominantę</strong>. Przed liczeniem warto sprawdzić, czy dane są
        podane pojedynczo, czy razem z liczebnościami poszczególnych wyników.
      </p>

      <p>
        <strong>Średnia arytmetyczna</strong> to suma wszystkich wyników podzielona
        przez ich liczbę:
      </p>
      <BlockMath math={String.raw`\bar{x}=\frac{x_1+x_2+\ldots+x_n}{n}`} />

      <p>
        <strong>Średnia ważona.</strong> Jeżeli wartościom
        <InlineMath math={String.raw`x_1,x_2,\ldots,x_n`} /> przypisano wagi
        <InlineMath math={String.raw`w_1,w_2,\ldots,w_n`} />, korzystamy ze wzoru:
      </p>
      <BlockMath
        math={String.raw`\bar{x}_w=\frac{w_1\cdot x_1+w_2\cdot x_2+\ldots+w_n\cdot x_n}{w_1+w_2+\ldots+w_n}`}
      />
      <p>
        Wagą może być liczebność wyniku albo grupy. Dlatego przy łączeniu grup
        mnożymy każdą średnią przez liczebność jej grupy, a nie liczymy zwykłej
        średniej ze średnich.
      </p>

      <p>
        <strong>Mediana.</strong> Po uporządkowaniu danych niemalejąco
        <InlineMath math={String.raw`a_1,a_2,\ldots,a_n`} /> wybieramy:
      </p>
      <BlockMath
        math={String.raw`\operatorname{Me}=\begin{cases}a_{\frac{n+1}{2}}, & n\ \text{nieparzyste},\\[6pt]\frac{1}{2}\left(a_{\frac{n}{2}}+a_{\frac{n}{2}+1}\right), & n\ \text{parzyste}.\end{cases}`}
      />
      <p>
        <strong>Dominanta (moda)</strong> to wartość występująca najczęściej.
        Dominant może być kilka, o ile pozostałe wartości występują rzadziej.
        Dlatego w zestawie <InlineMath math={String.raw`2,2,3,3`} /> dominanty
        nie ma, natomiast w zestawie <InlineMath math={String.raw`2,2,3,3,5`} />
        dominantami są <InlineMath math="2" /> i <InlineMath math="3" />.
      </p>

      <p>
        Uzupełniająco na karcie wzorów znajduje się również
        <strong> średnia geometryczna</strong> dodatnich liczb:
      </p>
      <BlockMath math={String.raw`g=\sqrt[n]{x_1\cdot x_2\cdot\ldots\cdot x_n}`} />
      <p>
        W tej części traktujemy ją jako krótki trening dodatkowy. Główny nacisk
        pozostaje na średniej arytmetycznej i ważonej, medianie oraz dominancie.
      </p>
    </div>
  );

  const examples = [
    {
      label: "Przykład 1",
      summary: "Średnia arytmetyczna",
      description: (
        <>
          Obliczamy średnią liczb <InlineMath math={String.raw`4,\ 7,\ 8,\ 9`} />.
        </>
      ),
      content: <BlockMath math={String.raw`\bar{x}=\frac{4+7+8+9}{4}=\frac{28}{4}=7`} />,
    },
    {
      label: "Przykład 2",
      summary: "Średnia ważona ocen",
      description: (
        <>
          Uczeń otrzymał ocenę <InlineMath math="5" /> za aktywność z wagą
          <InlineMath math="1" />, ocenę <InlineMath math="5" /> z kartkówki z
          wagą <InlineMath math="2" /> oraz ocenę <InlineMath math="3" /> ze
          sprawdzianu z wagą <InlineMath math="3" />.
        </>
      ),
      content: (
        <div>
          <p>
            Każdą ocenę mnożymy przez jej wagę. Sumę ważonych ocen dzielimy
            przez sumę wag.
          </p>
          <BlockMath math={String.raw`\bar{x}_w=\frac{5\cdot1+5\cdot2+3\cdot3}{1+2+3}=\frac{24}{6}=4`} />
        </div>
      ),
    },
    {
      label: "Przykład 3",
      summary: "Mediana i dominanta",
      description: (
        <>
          Wyznacz medianę i dominantę danych:
          {" "}<InlineMath math={String.raw`3,\ 8,\ 2,\ 5,\ 3,\ 7`} />.
        </>
      ),
      content: (
        <div>
          <p>
            Najpierw porządkujemy dane:
            {" "}<InlineMath math={String.raw`2,\ 3,\ 3,\ 5,\ 7,\ 8`} />. Dwa
            środkowe wyniki to <InlineMath math="3" /> i <InlineMath math="5" />.
          </p>
          <BlockMath math={String.raw`\mathrm{Me}=\frac{3+5}{2}=4,\qquad D=3`} />
          <p>
            Dominantą jest <InlineMath math="3" />, ponieważ występuje dwa razy,
            a każda pozostała wartość tylko raz.
          </p>
        </div>
      ),
    },
    {
      label: "Przykład 4",
      summary: "Średnia geometryczna — uzupełnienie",
      description: (
        <>
          Obliczamy średnią geometryczną liczb <InlineMath math="2" /> i
          <InlineMath math="8" />.
        </>
      ),
      content: <BlockMath math={String.raw`g=\sqrt{2\cdot8}=\sqrt{16}=4`} />,
    },
  ];

  return <TheoryExamplesRunner intro={intro} examples={examples} />;
}
